import { state } from './state.js';
import { mpState } from './multiplayer.js';
import { predictTrajectory, checkGoal, distSq, calculateGravityAcceleration } from './physics.js';
import { playSound, kick, playVoiceOver } from './audio.js';
import * as Tone from 'tone';
import {
    GAME_SPEED_MULTIPLIER,
    PLAY_AREA_WIDTH,
    PLAY_AREA_HEIGHT,
    COLLISION_DAMPING,
    MIN_SPEED_TO_STOP_IN_SPACE,
    MIN_SPEED_TO_STOP_AFTER_COLLISION,
    MIN_SPEED_TO_HALVE_AFTER_COLLISION,
    BUNKER_LATERAL_CHECK_GRACE_FRAC
} from './constants.js';


// --- Callback Registry to Prevent Circular Module Imports ---
let botCallbacks = {
    startMultiplayerMatch: null,
    checkAndResolveMPFinish: null,
    updateMPLeaderboardHUD: null,
    generateCourse: null,
    showMultiplayerToast: null
};

export function registerBotCallbacks(cbs) {
    botCallbacks = { ...botCallbacks, ...cbs };
}

// --- Funny Golfer Bot Names ---
const EASY_NAMES = [
    "SANDY", "OOBY", "BUMPY", "ASTRO", "CLUEY", "LOSTY",
    "DODGE", "DENT", "VAC", "COMET", "CHIP", "NOVICE"
];
const MEDIUM_NAMES = [
    "SLING", "PARTy", "SLIDE", "DUFF", "GRAV", "SWING",
    "PULSE", "MASH", "WIZ", "RING", "GLIDE", "CADET"
];
const HARD_NAMES = [
    "SNIPER", "BRAIN", "QUIRK", "HAL", "PRO", "CYAN", "LIME",
    "PINK", "GOLD", "ORANGE", "PURP", "TEAL", "INDIGO"
];

// --- Funny Bot Voice Quotes when starting ---
const INTRO_QUOTES = [
    "PREPARE TO BE ECLIPSED!",
    "GRAVITY IS MY CADDY!",
    "PAR IS FOR MORTALS!",
    "COMPUTING PERFECT SLINGSHOT...",
    "MAY THE GRAVITATIONAL FORCE BE WITH YOU!"
];

/**
 * Initializes a local Bot opponent inside mpState.players.
 * This completely bypasses PeerJS/networking.
 */
export function initLocalBotMatch(difficulty) {
    state.isBotGame = true;
    state.botDifficulty = difficulty; // 1 = Easy, 2 = Medium, 3 = Hard
    
    // Set up local player (0) and Bot players (1, 2, 3)
    mpState.isMultiplayer = true;
    mpState.role = 'host';
    mpState.playerId = 0;
    mpState.roomId = "LOCAL_BOT";
    mpState.connections = [];
    mpState.conn = null;
    mpState.peer = null;
    mpState.isConnected = false;
    
    // Set standard local player color details
    const localRgb = hexToRgb(state.customBallColor || '#FFFFFF');
    
    // Setup Bot colors and skin catalogs
    const botColors = [
        { r: 57, g: 255, b: 20 },   // Lime Green
        { r: 0, g: 255, b: 255 },   // Cyan
        { r: 255, g: 0, b: 255 },   // Neon Pink
        { r: 255, g: 215, b: 0 },   // Gold
        { r: 255, g: 100, b: 0 },   // Orange
        { r: 180, g: 0, b: 255 }    // Purple
    ];
    const skins = ["golf", "rave", "cat", "alien", "star", "stripes"];
    const trails = ["neon", "rainbow", "stardust", "sparkles"];
    
    mpState.players = [
        {
            id: 0,
            name: state.currentPlayerName,
            ready: true,
            finished: false,
            completedAll: false,
            strokes: 0,
            holeIndex: 0,
            finalStrokes: 0,
            finalTime: 0,
            ballState: { x: 0, y: 0, vx: 0, vy: 0, r: 8, isSinking: false, colorR: localRgb.r, colorG: localRgb.g, colorB: localRgb.b, skin: state.customBallSkin, trailEffect: state.customBallTrail, trail: [] }
        }
    ];
    
    // Generate 3 Bots
    for (let botIdx = 1; botIdx <= 3; botIdx++) {
        const nameList = difficulty === 1 ? EASY_NAMES : (difficulty === 2 ? MEDIUM_NAMES : HARD_NAMES);
        let botName = nameList[Math.floor(Math.random() * nameList.length)];
        // Ensure name is unique among existing players
        while (mpState.players.some(p => p.name === botName)) {
            botName = nameList[Math.floor(Math.random() * nameList.length)];
        }
        
        const bCol = botColors[(botIdx - 1) % botColors.length];
        const botSkin = skins[(botIdx - 1) % skins.length];
        const botTrail = trails[(botIdx - 1) % trails.length];
        
        mpState.players.push({
            id: botIdx,
            isBot: true,
            name: botName,
            ready: true,
            finished: false,
            completedAll: false,
            strokes: 0,
            holeIndex: 0,
            finalStrokes: 0,
            finalTime: 0,
            currentHoleStrokes: 0,
            botThinkingTimer: difficulty === 3 
                ? (800 + Math.random() * 800) 
                : (difficulty === 2 
                    ? (1000 + Math.random() * 1000) 
                    : (1500 + Math.random() * 1200)), // staggered thinking times
            botCalculatedShot: null,
            ballState: { x: 0, y: 0, vx: 0, vy: 0, r: 8, isSinking: false, colorR: bCol.r, colorG: bCol.g, colorB: bCol.b, skin: botSkin, trailEffect: botTrail, trail: [] }
        });
    }
    
    // Generate a new 9-hole Speedrun course
    if (botCallbacks.generateCourse) {
        const course = botCallbacks.generateCourse(9);
        mpState.courseLevels = course;
        
        // Hide Main Menu setup modals and launch match directly
        document.getElementById('startScreen').classList.remove('active');
        document.getElementById('mpSetupScreen').classList.remove('active');
        
        // Since startMultiplayerMatch will reset player metrics, we must preserve Bot name/skins/isBot properties
        const savedBots = JSON.parse(JSON.stringify(mpState.players.filter(p => p.isBot)));
        
        // Trigger main match start
        if (botCallbacks.startMultiplayerMatch) {
            botCallbacks.startMultiplayerMatch();
        }
        
        // Re-apply preserved Bot data that startMultiplayerMatch might have blanked out
        for (const savedBot of savedBots) {
            const botInGame = mpState.players.find(p => p.id === savedBot.id);
            if (botInGame) {
                botInGame.isBot = true;
                botInGame.name = savedBot.name;
                botInGame.ballState.colorR = savedBot.ballState.colorR;
                botInGame.ballState.colorG = savedBot.ballState.colorG;
                botInGame.ballState.colorB = savedBot.ballState.colorB;
                botInGame.ballState.skin = savedBot.ballState.skin;
                botInGame.ballState.trailEffect = savedBot.ballState.trailEffect;
                botInGame.currentHoleStrokes = 0;
                let initialThinkTime = 1000 + Math.random() * 1000; // Medium: 1.0s - 2.0s
                if (state.botDifficulty === 1) { // Easy
                    initialThinkTime = 1500 + Math.random() * 1200; // 1.5s - 2.7s
                } else if (state.botDifficulty === 3) { // Hard
                    initialThinkTime = 800 + Math.random() * 800; // Hard: 0.8s - 1.6s
                }
                botInGame.botThinkingTimer = initialThinkTime; // staggered think times to stagger their initial shoots
                botInGame.botCalculatedShot = null;
                
                // Position bot on the starting planet's surface
                resetBotBallToHoleStart(botInGame, true);
            }
        }
        
        // Trigger a crisp, short English or Japanese chiptune arcade VO phrase
        const randomBot = mpState.players.find(p => p.isBot);
        if (randomBot) {
            let voPhrase = "READY, GO!";
            if (difficulty === 1) {
                voPhrase = "GAMBATTE!"; // Easy: "Do your best!"
            } else if (difficulty === 3) {
                voPhrase = "IKUZO!"; // Hard: "Let's go!"
            }
            playVoiceOver(voPhrase);
        }
    } else {
        console.error("Local Bot Match failed to start: course generator not registered.");
    }
}

export function updateBot(deltaTime) {
    if (!state.isBotGame || state.currentPlayingMode !== 'multiplayer') return;
    
    let planningDoneThisFrame = false;
    for (const botPlayer of mpState.players) {
        if (!botPlayer.isBot || botPlayer.finished) continue;
        
        const botBall = botPlayer.ballState;
        if (!botBall) continue;
        
        // Fetch current level details
        const levelData = mpState.courseLevels[botPlayer.holeIndex];
        if (!levelData || !levelData.goal || !levelData.planets) continue;
        
        const goalPlanet = levelData.planets[levelData.goal.planetIndex];
        if (!goalPlanet) continue;
        
        const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(levelData.goal.angleOnPlanet);
        const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(levelData.goal.angleOnPlanet);
        
        // --- 1. Background Physics updates if the bot is on a different hole than the player ---
        if (botPlayer.holeIndex !== state.currentHoleIndex && botBall.moving && !botBall.isSinking) {
            const effectiveDelta = deltaTime * GAME_SPEED_MULTIPLIER;
            
            // Calculate aligned gravity using core physics engine formula
            const gravity = calculateGravityAcceleration(botBall.x, botBall.y, levelData.planets);
            botBall.vx += gravity.ax * effectiveDelta;
            botBall.vy += gravity.ay * effectiveDelta;
            botBall.x += botBall.vx * effectiveDelta;
            botBall.y += botBall.vy * effectiveDelta;
            
            if (Math.hypot(botBall.vx, botBall.vy) > 0.05) {
                if (!botBall.trail) botBall.trail = [];
                botBall.trail.push({ x: botBall.x, y: botBall.y, alpha: 1.0 });
                if (botBall.trail.length > 100) botBall.trail.shift();
            }
            
            // Resolve solid planet and bunker collisions in background
            for (const planet of levelData.planets) {
                let ballHandledForThisPlanet = false;
                
                // A. Bunker checks in background (sand trap sticky/stop physics)
                if (planet.bunkers && planet.bunkers.length > 0) {
                    for (const bunker of planet.bunkers) {
                        const bunkerWorldSurfaceX = planet.x + planet.r * Math.cos(bunker.angle);
                        const bunkerWorldSurfaceY = planet.y + planet.r * Math.sin(bunker.angle);
                        const effectiveBunkerRadiusForLateralCheck = bunker.radius + botBall.r * BUNKER_LATERAL_CHECK_GRACE_FRAC;
                        
                        if (distSq(botBall.x, botBall.y, bunkerWorldSurfaceX, bunkerWorldSurfaceY) < (effectiveBunkerRadiusForLateralCheck * effectiveBunkerRadiusForLateralCheck)) {
                            ballHandledForThisPlanet = true;
                            
                            const ballRadialDist = Math.hypot(botBall.x - planet.x, botBall.y - planet.y);
                            const d = Math.hypot(botBall.x - bunkerWorldSurfaceX, botBall.y - bunkerWorldSurfaceY);
                            if (ballRadialDist < planet.r - 2 && d >= bunker.radius - botBall.r) {
                                // Reposition exactly on the sand floor and stop
                                const dx = botBall.x - bunkerWorldSurfaceX;
                                const dy = botBall.y - bunkerWorldSurfaceY;
                                const distance = Math.hypot(dx, dy);
                                if (distance > 0) {
                                    const nx = dx / distance;
                                    const ny = dy / distance;
                                    botBall.x = bunkerWorldSurfaceX + nx * (bunker.radius - botBall.r);
                                    botBall.y = bunkerWorldSurfaceY + ny * (bunker.radius - botBall.r);
                                }
                                botBall.vx = 0;
                                botBall.vy = 0;
                                botBall.moving = false;
                                botBall.isInBunker = true;
                            }
                            break;
                        }
                    }
                }
                
                if (ballHandledForThisPlanet) continue;
                
                // B. Solid planet rock penetration and collision
                const dx = botBall.x - planet.x;
                const dy = botBall.y - planet.y;
                const dist = Math.hypot(dx, dy);
                if (dist < planet.r + botBall.r) {
                    const nx = dx / dist;
                    const ny = dy / dist;
                    const overlap = (planet.r + botBall.r) - dist;
                    botBall.x += nx * overlap * 1.01;
                    botBall.y += ny * overlap * 1.01;
                    
                    const velocityAlongNormal = botBall.vx * nx + botBall.vy * ny;
                    if (velocityAlongNormal < 0) {
                        const restitution = 0.9;
                        const impulse = -(1 + restitution) * velocityAlongNormal;
                        botBall.vx += impulse * nx;
                        botBall.vy += impulse * ny;
                        botBall.vx *= COLLISION_DAMPING;
                        botBall.vy *= COLLISION_DAMPING;
                    }
                    
                    // Surface stopped threshold
                    const speed = Math.hypot(botBall.vx, botBall.vy);
                    if (speed < MIN_SPEED_TO_HALVE_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) {
                        botBall.vx *= 0.5;
                        botBall.vy *= 0.5;
                        if (speed < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER || Math.hypot(botBall.vx, botBall.vy) < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) {
                            botBall.vx = 0;
                            botBall.vy = 0;
                            botBall.moving = false;
                        }
                    }
                }
            }
            
            // Stopped threshold in space vacuum (prevents infinite slow drift)
            const speed = Math.hypot(botBall.vx, botBall.vy);
            if (speed < MIN_SPEED_TO_STOP_IN_SPACE * GAME_SPEED_MULTIPLIER) {
                botBall.vx = 0;
                botBall.vy = 0;
                botBall.moving = false;
            }
            
            // Aligned dynamic out of bounds check in background
            const margin = botBall.r * 2;
            if (botBall.x < -margin || botBall.x > PLAY_AREA_WIDTH + margin || botBall.y < -margin || botBall.y > PLAY_AREA_HEIGHT + margin) {
                resetBotBallToHoleStart(botPlayer, false, true);
                console.log(`[Bot AI] Background ball went out of bounds and was reset to last resting point.`);
            }
        }
        
        // --- 2. Handle Sinking / Goal detection ---
        if (!botBall.isSinking) {
            const distanceToGoal = Math.hypot(botBall.x - goalHoleX, botBall.y - goalHoleY);
            if (distanceToGoal < levelData.goal.holeRadius + botBall.r * 0.25) {
                // Bot Sinks Ball!
                botBall.isSinking = true;
                botBall.moving = false;
                botBall.vx = 0;
                botBall.vy = 0;
                
                // Commit strokes
                botPlayer.strokes += botPlayer.currentHoleStrokes || 1;
                botPlayer.currentHoleStrokes = 0;
                
                console.log(`${botPlayer.name} sank ball on Hole ${botPlayer.holeIndex + 1}! Total strokes: ${botPlayer.strokes}`);
                
                // Transition to next hole (instant if in background, delayed if on active screen to let animation finish)
                const transitionDelay = (botPlayer.holeIndex === state.currentHoleIndex) ? 1200 : 0;
                setTimeout(() => {
                    if (botPlayer.holeIndex < 8) {
                        botPlayer.holeIndex++;
                        resetBotBallToHoleStart(botPlayer, true);
                    } else {
                        // Finished the whole course!
                        botPlayer.finished = true;
                        botPlayer.completedAll = true;
                        botPlayer.finalStrokes = botPlayer.strokes;
                        
                        const totalTime = (performance.now() - mpState.startTime) / 1000;
                        botPlayer.finalTime = totalTime;
                        
                        botBall.x = -9999;
                        botBall.y = -9999;
                        
                        // Trigger dynamic rank suffix completed alerts
                        const rank = mpState.players.filter(p => p.finished && p.completedAll).length;
                        let placeSuffix = "th";
                        if (rank === 1) placeSuffix = "st";
                        else if (rank === 2) placeSuffix = "nd";
                        else if (rank === 3) placeSuffix = "rd";
                        
                        if (botCallbacks.showMultiplayerToast) {
                            botCallbacks.showMultiplayerToast(`🏁 ${botPlayer.name.toUpperCase()} FINISHED! (${rank}${placeSuffix} Place)`, "text-pink-400 border-pink-500/30");
                        }
                        
                        console.log(`${botPlayer.name} finished the race in ${totalTime.toFixed(1)}s!`);
                        if (botCallbacks.checkAndResolveMPFinish) {
                            botCallbacks.checkAndResolveMPFinish();
                        }
                    }
                    if (botCallbacks.updateMPLeaderboardHUD) {
                        botCallbacks.updateMPLeaderboardHUD();
                    }
                }, transitionDelay);
            }
        }
        
        // --- 3. Handle Aiming & Shooting when stationary ---
        if (botBall.recentlyBumpedTimer > 0 && (botPlayer.botCalculatedShot || botPlayer.botThinkingTimer > 0 || botPlayer.botPlanningState)) {
            // Bumped! Cancel pre-calculated shot, planning state and reset aiming timer to a fresh staggered delay
            botPlayer.botCalculatedShot = null;
            botPlayer.botPlanningState = null;
            botPlayer.botThinkingTimer = state.botDifficulty === 3 ? (1000 + Math.random() * 1000) : (1800 + Math.random() * 1200);
            console.log(`[Bot AI] ${botPlayer.name} was bumped! Cancelling calculated shot/planning and resetting timer.`);
        }
        
        if (!botBall.moving && !botBall.isSinking) {
            if (botPlayer.botThinkingTimer === undefined) botPlayer.botThinkingTimer = 0;
            
            // 1. Initialize planning state and thinking timer if not present and no calculated shot
            if (!botPlayer.botCalculatedShot && !botPlayer.botPlanningState) {
                const distToGoal = Math.hypot(botBall.x - goalHoleX, botBall.y - goalHoleY);
                const directAngle = Math.atan2(goalHoleY - botBall.y, goalHoleX - botBall.x);
                let angleSteps = 36;
                let powerSteps = 6;
                
                if (botPlayer.holeIndex !== state.currentHoleIndex) {
                    // Background bots: extremely low planning overhead
                    angleSteps = 12;
                    powerSteps = 3;
                } else {
                    if (state.botDifficulty === 1) { // Easy
                        angleSteps = 16;
                        powerSteps = 4;
                    } else if (state.botDifficulty === 2) { // Medium
                        angleSteps = 36;
                        powerSteps = 6;
                    } else { // Hard
                        angleSteps = 60;
                        powerSteps = 8;
                    }
                }
                
                const maxDrag = state.currentDynamicMaxDrag || 150;
                const powerOptions = [];
                for (let p = 1; p <= powerSteps; p++) {
                    powerOptions.push(30 + (maxDrag - 30) * (p / powerSteps));
                }

                let currentBunker = null;
                let currentBunkerPlanet = null;
                if (botBall.isInBunker && levelData.planets) {
                    for (const p of levelData.planets) {
                        if (p.bunkers && p.bunkers.length > 0) {
                            for (const bunker of p.bunkers) {
                                const bunkerWorldSurfaceX = p.x + p.r * Math.cos(bunker.angle);
                                const bunkerWorldSurfaceY = p.y + p.r * Math.sin(bunker.angle);
                                const checkRadius = bunker.radius + 15;
                                if (distSq(botBall.x, botBall.y, bunkerWorldSurfaceX, bunkerWorldSurfaceY) < checkRadius * checkRadius) {
                                    currentBunker = bunker;
                                    currentBunkerPlanet = p;
                                    break;
                                }
                            }
                        }
                        if (currentBunker) break;
                    }
                }

                botPlayer.botPlanningState = {
                    angleIndex: 0,
                    angleSteps: angleSteps,
                    powerSteps: powerSteps,
                    powerOptions: powerOptions,
                    maxDrag: maxDrag,
                    directAngle: directAngle,
                    bestShot: null,
                    bestScore: -Infinity,
                    currentBunker: currentBunker,
                    currentBunkerPlanet: currentBunkerPlanet
                };

                let thinkTime = 1200 + Math.random() * 800; // standard Medium
                if (state.botDifficulty === 1) { // Easy
                    thinkTime = 2000 + Math.random() * 1000;
                } else if (state.botDifficulty === 3) { // Hard
                    thinkTime = 800 + Math.random() * 600;
                }
                if (distToGoal < 220) {
                    thinkTime = state.botDifficulty === 3 ? (400 + Math.random() * 400) : (600 + Math.random() * 600);
                } else if (botPlayer.currentHoleStrokes === 0) {
                    if (state.botDifficulty === 1) {
                        thinkTime = 2000 + Math.random() * 1200;
                    } else if (state.botDifficulty === 2) {
                        thinkTime = 1200 + Math.random() * 1000;
                    } else { // Hard
                        thinkTime = 800 + Math.random() * 800;
                    }
                }
                botPlayer.botThinkingTimer = thinkTime;
            }

            // 2. Slice the trajectory predictive search across frames to keep mobile devices butter-smooth
            if (botPlayer.botPlanningState && botPlayer.botPlanningState.angleIndex < botPlayer.botPlanningState.angleSteps) {
                if (planningDoneThisFrame) {
                    continue; // Defer to next frame to keep load evenly spread
                }
                planningDoneThisFrame = true;

                const ps = botPlayer.botPlanningState;
                // Stagger background bots planning to 1 angle/frame, and active screen bots to 2 angles/frame to eliminate any parallel micro-stutters
                const isActiveHole = botPlayer.holeIndex === state.currentHoleIndex;
                const chunkCount = isActiveHole ? 2 : 1; 
                const endAngleIndex = Math.min(ps.angleSteps, ps.angleIndex + chunkCount);
                
                for (let a = ps.angleIndex; a < endAngleIndex; a++) {
                    const searchJitter = (Math.random() - 0.5) * 0.015;
                    const testAngle = ps.directAngle + (a * Math.PI * 2) / ps.angleSteps + searchJitter;
                    
                    if (ps.currentBunker) {
                        let diff = testAngle - ps.currentBunker.angle;
                        diff = Math.atan2(Math.sin(diff), Math.cos(diff));
                        if (Math.abs(diff) > 1.4) {
                            continue; // Skip shooting inward into the bunker planet
                        }
                    }
                    
                    for (const dragPower of ps.powerOptions) {
                        const powerRatio = dragPower / ps.maxDrag;
                        const basePowerForSqrt = powerRatio * 18;
                        let powerNormalized = Math.sqrt(Math.max(0, basePowerForSqrt)) * 1.0;
                        
                        if (botBall.isInBunker) {
                            powerNormalized *= 0.7;
                        }
                        
                        const vx = Math.cos(testAngle) * powerNormalized * 100;
                        const vy = Math.sin(testAngle) * powerNormalized * 100;
                        
                        // Predict path dynamically (fewer steps for background bots to save CPU)
                        const maxSteps = botPlayer.holeIndex === state.currentHoleIndex ? 350 : 120;
                        const steps = predictTrajectory(botBall.x, botBall.y, vx, vy, maxSteps, 0.016, -1, false, levelData.planets);
                        
                        if (steps.length === 0) continue;
                        
                        let minDistanceToGoal = Infinity;
                        let sinkStep = -1;
                        
                        for (let stepIdx = 0; stepIdx < steps.length; stepIdx++) {
                            const pt = steps[stepIdx];
                            const d2 = distSq(pt.x, pt.y, goalHoleX, goalHoleY);
                            const d = Math.sqrt(d2);
                            
                            if (d < minDistanceToGoal) {
                                minDistanceToGoal = d;
                            }
                            
                            if (d < 16) {
                                sinkStep = stepIdx;
                                break;
                            }
                        }
                        
                        let score = 0;
                        if (sinkStep !== -1) {
                            score = 100000 - sinkStep * 100;
                        } else {
                            const finalPt = steps[steps.length - 1];
                            const finalDist = Math.hypot(finalPt.x - goalHoleX, finalPt.y - goalHoleY);
                            score = -finalDist - minDistanceToGoal * 0.2 - dragPower * 0.8;
                            
                            if (finalPt.x < 20 || finalPt.x > PLAY_AREA_WIDTH - 20 || finalPt.y < 20 || finalPt.y > PLAY_AREA_HEIGHT - 20) {
                                score -= 15000;
                            }
                            
                            if (ps.currentBunker && ps.currentBunkerPlanet) {
                                const bunkerWorldSurfaceX = ps.currentBunkerPlanet.x + ps.currentBunkerPlanet.r * Math.cos(ps.currentBunker.angle);
                                const bunkerWorldSurfaceY = ps.currentBunkerPlanet.y + ps.currentBunkerPlanet.r * Math.sin(ps.currentBunker.angle);
                                const distToBunkerCenter = Math.hypot(finalPt.x - bunkerWorldSurfaceX, finalPt.y - bunkerWorldSurfaceY);
                                const radialDistFromPlanet = Math.hypot(finalPt.x - ps.currentBunkerPlanet.x, finalPt.y - ps.currentBunkerPlanet.y);
                                
                                if (radialDistFromPlanet < ps.currentBunkerPlanet.r && distToBunkerCenter < ps.currentBunker.radius + 5) {
                                    score -= 50000;
                                }
                            }
                        }
                        
                        if (score > ps.bestScore) {
                            ps.bestScore = score;
                            ps.bestShot = { angle: testAngle, dragPower: dragPower };
                        }
                    }
                }
                
                ps.angleIndex = endAngleIndex;
                
                // If planning is completed, compile the final shot with difficulty noise!
                if (ps.angleIndex >= ps.angleSteps) {
                    let best = ps.bestShot || { angle: ps.directAngle, dragPower: 70 };
                    
                    let finalAngle = best.angle;
                    let finalPower = best.dragPower;
                    
                    if (state.botDifficulty === 1) { // Easy
                        const angleNoise = (Math.random() - 0.5) * 0.48;
                        const powerNoise = 1.0 + (Math.random() - 0.5) * 0.40;
                        finalAngle += angleNoise;
                        finalPower = Math.min(ps.maxDrag, Math.max(30, finalPower * powerNoise));
                    } else if (state.botDifficulty === 2) { // Medium
                        const angleNoise = (Math.random() - 0.5) * 0.14;
                        const powerNoise = 1.0 + (Math.random() - 0.5) * 0.11;
                        finalAngle += angleNoise;
                        finalPower = Math.min(ps.maxDrag, Math.max(30, finalPower * powerNoise));
                    } else { // Hard
                        const angleNoise = (Math.random() - 0.5) * 0.11;
                        const powerNoise = 1.0 + (Math.random() - 0.5) * 0.07;
                        finalAngle += angleNoise;
                        finalPower = Math.min(ps.maxDrag, Math.max(30, finalPower * powerNoise));
                    }
                    
                    const finalPowerRatio = finalPower / ps.maxDrag;
                    const finalBasePower = finalPowerRatio * 18;
                    let finalPowerNorm = Math.sqrt(Math.max(0, finalBasePower)) * 1.0;
                    
                    if (botBall.isInBunker) {
                        finalPowerNorm *= 0.7;
                    }
                    
                    const finalVx = Math.cos(finalAngle) * finalPowerNorm * 100;
                    const finalVy = Math.sin(finalAngle) * finalPowerNorm * 100;
                    
                    botPlayer.botCalculatedShot = { vx: finalVx, vy: finalVy };
                    botPlayer.botPlanningState = null; // Done planning!
                }
            }

            if (botPlayer.botThinkingTimer > 0) {
                // Decr thinking timer
                botPlayer.botThinkingTimer -= deltaTime * 1000;
                continue;
            }

            if (botPlayer.botCalculatedShot) {
                // Fire the shot!
                const shot = botPlayer.botCalculatedShot;
                botPlayer.botCalculatedShot = null;
                
                let finalVx = shot.vx;
                let finalVy = shot.vy;
                
                // Check if we are repeating a shot from almost the exact same starting position (loop detection)
                if (botPlayer.lastShotStartX !== undefined && botPlayer.lastShotStartY !== undefined) {
                    const distMovedSq = distSq(botBall.x, botBall.y, botPlayer.lastShotStartX, botPlayer.lastShotStartY);
                    if (distMovedSq < 9) { // Less than 3px movement!
                        // Apply a random perturbation (±16 degrees and ±15% power) to escape the loop
                        const angleOffset = (Math.random() - 0.5) * 0.56; 
                        const currentAngle = Math.atan2(finalVy, finalVx);
                        const speed = Math.hypot(finalVx, finalVy);
                        const newSpeed = speed * (0.85 + Math.random() * 0.30);
                        
                        finalVx = Math.cos(currentAngle + angleOffset) * newSpeed;
                        finalVy = Math.sin(currentAngle + angleOffset) * newSpeed;
                        console.log(`[Bot AI] Stuck loop detected for ${botPlayer.name}! Perturbing shot by ${(angleOffset * 180 / Math.PI).toFixed(1)}° and power to escape.`);
                    }
                }
                
                // Record last shot starting coordinates
                botPlayer.lastShotStartX = botBall.x;
                botPlayer.lastShotStartY = botBall.y;
                
                botBall.vx = finalVx;
                botBall.vy = finalVy;
                botBall.moving = true;
                botBall.isInBunker = false; // Reset bunker state upon shooting!
                botBall.impactTimer = 1.3;
                botBall.angularVelocity = (Math.random() > 0.5 ? 1 : -1) * Math.hypot(finalVx, finalVy) * 0.04;
                
                // Record shot count
                botPlayer.currentHoleStrokes = (botPlayer.currentHoleStrokes || 0) + 1;
                
                // Immediately trigger a HUD leaderboard update to show the new shot count in real time!
                if (botCallbacks.updateMPLeaderboardHUD) {
                    botCallbacks.updateMPLeaderboardHUD();
                }
                
                // Trigger flash for the bot player row!
                if (typeof window.triggerHUDHitFlash === 'function') {
                    window.triggerHUDHitFlash(botPlayer.id);
                }
                
                // Play a standard swing click sound locally!
                playSound(kick, "C2", "8n", Tone.now(), 0.6);
                console.log(`${botPlayer.name} shot ball with velocity: (${finalVx.toFixed(1)}, ${finalVy.toFixed(1)})`);
                continue;
            }
        }
    }
}

/**
 * Places the bot's ball back on the tee of its target hole index.
 */
export function resetBotBallToHoleStart(botPlayer, resetStrokes = false, isOOB = false) {
    if (isOOB && botPlayer.lastShotStartX !== undefined && botPlayer.lastShotStartY !== undefined) {
        botPlayer.ballState.x = botPlayer.lastShotStartX;
        botPlayer.ballState.y = botPlayer.lastShotStartY;
        botPlayer.ballState.vx = 0;
        botPlayer.ballState.vy = 0;
        botPlayer.ballState.moving = false;
        botPlayer.ballState.isSinking = false;
        botPlayer.ballState.trail = [];
        botPlayer.ballState.spawnProtectionTimer = 30;
        if (resetStrokes) {
            botPlayer.currentHoleStrokes = 0;
        }
        
        // Trigger warp effect on screen if bot spawns on the current screen hole
        if (botPlayer.holeIndex === state.currentHoleIndex) {
            if (typeof window.triggerOpponentPopIn === 'function') {
                window.triggerOpponentPopIn(botPlayer.ballState.x, botPlayer.ballState.y);
            }
        }
        return;
    }

    const levelData = mpState.courseLevels[botPlayer.holeIndex];
    if (!levelData || !levelData.planets || levelData.planets.length === 0) return;
    
    const startPlanet = levelData.planets[0];
    const spawnAngle = levelData.spawnAngle !== undefined ? levelData.spawnAngle : 0;
    
    // Dynamic slot spawn spread offset matching standard multiplayer layout (exactly >= 1.5 balls physical distance)
    const slotIdx = botPlayer.id; // 0 = player, 1, 2, 3 = bots
    const spread = 28 / startPlanet.r;
    const finalAngle = spawnAngle + (slotIdx - 1.5) * spread; 
    const teeHeight = 5; // standard tee tip offset
    
    const tipX = startPlanet.x + Math.cos(finalAngle) * (startPlanet.r + teeHeight);
    const tipY = startPlanet.y + Math.sin(finalAngle) * (startPlanet.r + teeHeight);
    
    botPlayer.ballState.x = tipX + Math.cos(finalAngle) * 8; // 8 = ball radius
    botPlayer.ballState.y = tipY + Math.sin(finalAngle) * 8;
    botPlayer.ballState.vx = 0;
    botPlayer.ballState.vy = 0;
    botPlayer.ballState.moving = false;
    botPlayer.ballState.isSinking = false;
    botPlayer.ballState.trail = [];
    botPlayer.ballState.spawnProtectionTimer = 30;
    
    if (resetStrokes) {
        botPlayer.currentHoleStrokes = 0;
    }
    
    // Trigger warp effect on screen if bot spawns on the current screen hole
    if (botPlayer.holeIndex === state.currentHoleIndex) {
        if (typeof window.triggerOpponentPopIn === 'function') {
            window.triggerOpponentPopIn(botPlayer.ballState.x, botPlayer.ballState.y);
        }
    }
}


// Utility: convert hex color to RGB
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
}
